/**
 * @component TimerFloatingPanel (#996 Multi-Timer)
 * @description Floating Bubble + Panel rechts unten. Ersetzt ZeiterfassungTimerBubble (#989/#994).
 *
 * Modell A: 1 running + N paused. Eingeklappt = Bubble mit Counter; ausgeklappt = Liste.
 * Pollt /api/zeiterfassung/timer/offene alle 10s, tickt Sekunden-Counter lokal.
 * Aktionen: Pause / Resume / Stop / Quick-Edit / Bulk-Stop-Zombies.
 *
 * Mobile: <768px → Vollbild-Modal beim Auf-Klick.
 *
 * @since v2.432.0 (#996)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Pause, Play, Square, X, ChevronDown, AlertTriangle, Edit3, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API = import.meta.env.VITE_API_URL || '';

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }
function formatTimerSec(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}
function formatHm(min) {
  if (!min || min < 0) min = 0;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function computeElapsedSec(timer, nowMs) {
  if (!timer || !timer.start_at) return 0;
  const startMs = new Date(timer.start_at).getTime();
  const accPauseMs = (parseInt(timer.accumulated_pause_min, 10) || 0) * 60000;
  const refMs = timer.paused_at ? new Date(timer.paused_at).getTime() : nowMs;
  return Math.max(0, Math.floor((refMs - startMs - accPauseMs) / 1000));
}
function pausedSince(timer) {
  if (!timer?.paused_at) return null;
  const ms = Date.now() - new Date(timer.paused_at).getTime();
  return Math.max(0, Math.floor(ms / 60000));
}

export default function TimerFloatingPanel() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState({ offene: [], heute_gesamt_min: 0, heute_n: 0 });
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const wrapRef = useRef(null);

  // Gating: only mount for users mit Zugriff auf Zeiterfassung-Modul (#1002)
  // Permission-basiert statt mitarbeiter.zeiterfassung_aktiv (das gilt nur als Override für portal_mitarbeiter).
  const zePerm = user?.permissions?.zeiterfassung;
  const eligible = !!(user && zePerm && zePerm !== 'none' && !['portal_kunde', 'kunde', 'jv_partner'].includes(user.rolle));
  // Don't render on public/standalone routes (kiosk, packstation, TV displays, login)
  const path = location.pathname;
  const isPublicRoute = path === '/login' || path === '/kiosk' || path.startsWith('/packstation')
    || path === '/lager-tv' || path === '/abwesenheit-display' || path === '/kunden-rotation/tv'
    || path.startsWith('/aufnahmen/display/') || path.startsWith('/p/') || path === '/work'
    || path.startsWith('/work/') || path === '/karriere' || path.startsWith('/invite/')
    || path.startsWith('/feedback/') || path.startsWith('/case-action/')
    || path.startsWith('/calculator') || path === '/jobs' || path.startsWith('/flex/');

  const fetchOffene = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/zeiterfassung/timer/offene`, { credentials: 'include' });
      if (!res.ok) { setData({ offene: [], heute_gesamt_min: 0, heute_n: 0 }); return; }
      const d = await res.json();
      setData({
        offene: d.offene || [],
        heute_gesamt_min: d.heute_gesamt_min || 0,
        heute_n: d.heute_n || 0,
      });
    } catch {
      setData({ offene: [], heute_gesamt_min: 0, heute_n: 0 });
    }
  }, []);

  useEffect(() => {
    if (!eligible || isPublicRoute) return;
    fetchOffene();
    const iv = setInterval(fetchOffene, 10000);
    return () => clearInterval(iv);
  }, [fetchOffene, eligible, isPublicRoute]);

  // Cross-view sync: refresh on weship:timer-changed (e.g. dispatched from Mein-Tag)
  useEffect(() => {
    if (!eligible || isPublicRoute) return;
    const onChanged = () => fetchOffene();
    window.addEventListener('weship:timer-changed', onChanged);
    return () => window.removeEventListener('weship:timer-changed', onChanged);
  }, [fetchOffene, eligible, isPublicRoute]);

  // Tick (sec) — nur wenn ein running Timer existiert
  useEffect(() => {
    const hasRunning = data.offene.some(it => !it.paused_at);
    if (!hasRunning) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [data.offene]);

  // Click-outside schließen (nur Desktop)
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const offene = data.offene;
  const total = offene.length;
  if (!eligible || isPublicRoute) return null;
  if (total === 0) return null;

  const running = offene.find(it => !it.paused_at) || null;
  const zombies = offene.filter(it => it.is_zombie);
  const overSoftLimit = total > 10;

  async function callAction(path, body = {}, refresh = true) {
    try {
      const res = await fetch(`${API}${path}`, {
        method: path.includes('quick-edit') ? 'PATCH' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (refresh) await fetchOffene();
      // Notify Mein-Tag + other listeners (#1001 state-sync)
      try { window.dispatchEvent(new CustomEvent('weship:timer-changed')); } catch { /* ignore */ }
      return res.ok;
    } catch { return false; }
  }

  async function handlePauseRunning() {
    if (!running || busyId) return;
    setBusyId(running.id);
    await callAction('/api/zeiterfassung/timer/pause', { id: running.id });
    setBusyId(null);
  }
  async function handleResume(timerId) {
    if (busyId) return;
    setBusyId(timerId);
    await callAction('/api/zeiterfassung/timer/resume', { id: timerId });
    setBusyId(null);
  }
  async function handleStop(timerId) {
    if (busyId) return;
    setBusyId(timerId);
    await callAction('/api/zeiterfassung/timer/stop', { id: timerId });
    setBusyId(null);
  }
  async function handleBulkStopZombies() {
    if (!zombies.length) return;
    setBusyId('zombies');
    await callAction('/api/zeiterfassung/timer/bulk-stop', { zombies: true });
    setBusyId(null);
  }
  async function handleQuickEditSave(timerId) {
    setBusyId(timerId);
    await callAction(`/api/zeiterfassung/timer/${timerId}/quick-edit`, { beschreibung: editText });
    setEditId(null);
    setEditText('');
    setBusyId(null);
  }

  function handleOpenItem(item) {
    if (item.case_id) navigate(`/cases/${item.case_id}`);
    else navigate('/zeiterfassung?tab=tag');
  }

  // Eingeklappte Bubble
  if (!open) {
    return (
      <button
        ref={wrapRef}
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full shadow-lg border border-[var(--border-card,#E5E7EB)] bg-white dark:bg-[#1A1A2E] hover:shadow-xl transition"
        title={t('timerPanel.openTitle')}
      >
        {running ? (
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
          </span>
        ) : (
          <Pause className="w-3.5 h-3.5 text-amber-600" />
        )}
        <Clock className="w-4 h-4 text-[var(--text-secondary,#4B5563)]" />
        {running && (
          <span className="text-[13px] font-semibold tabular-nums text-[var(--text-primary,#1A1A2E)]">
            {formatTimerSec(computeElapsedSec(running, now))}
          </span>
        )}
        <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#338edc] text-white text-[11px] font-bold">
          {total}
        </span>
        {zombies.length > 0 && (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
        )}
      </button>
    );
  }

  // Ausgeklappt — Panel
  return (
    <>
      {/* Mobile Backdrop */}
      <div className="md:hidden fixed inset-0 z-[59] bg-black/40" onClick={() => setOpen(false)} />
      <div
        ref={wrapRef}
        className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-40px)] md:w-[380px] max-h-[calc(100vh-80px)] rounded-[14px] border border-[var(--border-card,#E5E7EB)] bg-white dark:bg-[#1A1A2E] shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] dark:bg-[#10101F]">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[var(--text-secondary,#4B5563)]" />
            <span className="text-[14px] font-semibold text-[var(--text-primary,#1A1A2E)]">
              {t('timerPanel.title')}
            </span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#338edc] text-white text-[11px] font-bold">
              {total}
            </span>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[var(--bg-card-hover,#F3F4F6)] text-[var(--text-tertiary,#6B7280)]">
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Soft-Limit-Warning */}
        {overSoftLimit && (
          <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 text-[12px] text-amber-800 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            {t('timerPanel.softLimit', { N: total })}
          </div>
        )}

        {/* Liste */}
        <div className="overflow-y-auto flex-1 divide-y divide-[var(--border-table,#F3F4F6)]">
          {offene.map(item => {
            const isPaused = !!item.paused_at;
            const isZombie = !!item.is_zombie;
            const elapsed = computeElapsedSec(item, now);
            const pSince = pausedSince(item);
            return (
              <div key={item.id} className="px-3 py-2.5 hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[#10101F]">
                <div className="flex items-start gap-2">
                  <span className="mt-1 flex-shrink-0">
                    {isZombie ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                    ) : isPaused ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    ) : (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                      </span>
                    )}
                  </span>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => editId !== item.id && handleOpenItem(item)}>
                    <div className="text-[13px] font-semibold text-[var(--text-primary,#1A1A2E)] truncate">
                      {item.kunde_name || t('timerPanel.kundenlos')}
                      {item.kategorie_name && <span className="font-normal text-[var(--text-tertiary,#6B7280)]"> · {item.kategorie_name}</span>}
                    </div>
                    {editId === item.id ? (
                      <div className="mt-1 flex items-center gap-1">
                        <input
                          type="text" autoFocus value={editText}
                          onChange={e => setEditText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleQuickEditSave(item.id);
                            if (e.key === 'Escape') { setEditId(null); setEditText(''); }
                          }}
                          className="flex-1 px-2 py-1 text-[12px] rounded-[6px] border border-[var(--border-card,#E5E7EB)] bg-white dark:bg-[#10101F] text-[var(--text-primary,#1A1A2E)]"
                        />
                        <button
                          onClick={() => handleQuickEditSave(item.id)}
                          className="p-1 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                          title={t('common.save')}
                        ><Check className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <div className="text-[12px] text-[var(--text-secondary,#4B5563)] truncate" title={item.beschreibung || ''}>
                        {item.beschreibung || <span className="italic text-[var(--text-tertiary,#6B7280)]">{t('timerPanel.keineBeschreibung')}</span>}
                      </div>
                    )}
                    <div className="mt-1 text-[11px] text-[var(--text-tertiary,#6B7280)] tabular-nums">
                      {isPaused ? (
                        isZombie ? (
                          <span className="text-amber-700 dark:text-amber-300 font-medium">
                            {t('timerPanel.zombieSeit', { DAYS: Math.floor((pSince || 0) / 1440) })}
                          </span>
                        ) : (
                          <>{t('timerPanel.pausiertSeit')} · {formatHm(pSince || 0)}</>
                        )
                      ) : (
                        <span className="text-blue-700 dark:text-blue-300 font-semibold">
                          {formatTimerSec(elapsed)} {t('timerPanel.laeuft')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {editId === item.id ? null : (
                      <button
                        onClick={() => { setEditId(item.id); setEditText(item.beschreibung || ''); }}
                        className="p-1 rounded hover:bg-[var(--bg-card-hover,#F3F4F6)] text-[var(--text-tertiary,#6B7280)]"
                        title={t('timerPanel.quickEdit')}
                      ><Edit3 className="w-3 h-3" /></button>
                    )}
                    {isPaused ? (
                      <button
                        disabled={busyId === item.id}
                        onClick={() => handleResume(item.id)}
                        className="p-1 rounded text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                        title={t('timerPanel.resume')}
                      ><Play className="w-3.5 h-3.5" /></button>
                    ) : (
                      <button
                        disabled={busyId === item.id}
                        onClick={handlePauseRunning}
                        className="p-1 rounded text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50"
                        title={t('timerPanel.pause')}
                      ><Pause className="w-3.5 h-3.5" /></button>
                    )}
                    <button
                      disabled={busyId === item.id}
                      onClick={() => handleStop(item.id)}
                      className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                      title={t('timerPanel.stop')}
                    ><Square className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] dark:bg-[#10101F] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[var(--text-secondary,#4B5563)]">
            {t('timerPanel.heute')}: <span className="font-semibold text-[var(--text-primary,#1A1A2E)] tabular-nums">{formatHm(data.heute_gesamt_min)}</span>
            {data.heute_n > 0 && <span className="text-[var(--text-tertiary,#6B7280)]"> · {data.heute_n} {t('timerPanel.eintraege')}</span>}
          </div>
          {zombies.length > 0 ? (
            <button
              disabled={busyId === 'zombies'}
              onClick={handleBulkStopZombies}
              className="text-[11px] px-2 py-1 rounded-[6px] bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60 font-medium disabled:opacity-50"
            >
              {t('timerPanel.bulkStopZombies', { N: zombies.length })}
            </button>
          ) : (
            <button
              onClick={() => { setOpen(false); navigate('/zeiterfassung?tab=tag'); }}
              className="text-[11px] text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]"
            >
              {t('timerPanel.toTag')} →
            </button>
          )}
        </div>
      </div>
    </>
  );
}
